import {
  ColumnType,
  type Column,
  type RawColumn,
  type RelatedColumn,
} from "./column";
import type {
  AndOrCondition,
  ConditionType,
  PropertyCondition,
} from "./condition";

export type TraitDefinition = {
  name: string;
  type: TraitType;
  config: TraitConfig;
  relationshipId: string | null;
  modelId: string;
  segmentId: string;
};

export enum TraitType {
  Count = "count",
  Sum = "sum",
  Average = "average",
  RawSql = "raw_sql",
  LeastFrequent = "least_frequent",
  MostFrequent = "most_frequent",
  First = "first",
  Last = "last",
  Formula = "formula",
  List = "list",
  Predictive = "predictive",
  TrackedAudienceMembership = "tracked_audience_membership",
  RealtimeFormula = "realtime_formula",
}

export function isTraitType(value: unknown): value is TraitType {
  return Object.values(TraitType).includes(value as TraitType);
}

export type TraitConfig =
  | CountTraitConfig
  | SumTraitConfig
  | AverageTraitConfig
  | CountDedupedTraitConfig
  | OrderDedupedTraitConfig
  | RawSqlTraitConfig
  | FormulaTraitConfig
  | ListTraitConfig
  | PredictiveTraitConfig
  | TrackedAudienceMembershipTraitConfig
  | RealtimeFormulaTraitConfig;

export type TraitCondition = {
  type: ConditionType.And | ConditionType.Or;
  conditions: AndOrCondition<PropertyCondition>[];
};

/**
 * The time window of a realtime aggregate trait — the only thing that windows
 * a trait (filter conditions never are). Sliding's value matches RTA's
 * WindowValue so its window control can be reused; only windows up to 24h are
 * servable without warehouse backing.
 */
export const REALTIME_TRAIT_SLIDING_WINDOW_UNITS = [
  "minute",
  "hour",
  "day",
] as const;

export type RealtimeTraitSlidingWindowUnit =
  (typeof REALTIME_TRAIT_SLIDING_WINDOW_UNITS)[number];

export type RealtimeTraitWindow =
  | { type: "lifetime" }
  | {
      type: "sliding";
      value: {
        direction: "backward";
        interval: RealtimeTraitSlidingWindowUnit;
        quantity: number;
      };
    };

export type RealtimeListOperationType = "insert" | "remove" | "replace";

/**
 * One operation binding for a realtime list trait. insert/remove read
 * scalar elements; replace reads array columns holding the full new list.
 * Every present column's values flatten into the same list, in column
 * order. Each event model carries at most one operation; the event's event
 * model selects it.
 */
export type RealtimeListOperation = {
  eventModelId: string;
  op: RealtimeListOperationType;
  /** Raw column names on the operation's event model. */
  columns: string[];
  /** Admission filter: events that fail it don't apply this operation. */
  conditions?: TraitCondition[];
};

export const REALTIME_LIST_MAX_ITEMS = 25;
export const REALTIME_LIST_MAX_ITEM_BYTES = 32;

// Mirrors isListElementType / isListArrayType in
// realtime/internal/trait/definition.go: a config the worker skips unloads
// the trait and the orphan pruner wipes its accumulated user state, so the
// API and the UI must reject everything the worker rejects. Declared as
// string sets because consumers compare raw column-type strings, but built
// from the enum so renames stay type-checked.
export const REALTIME_LIST_ELEMENT_COLUMN_TYPES: ReadonlySet<string> = new Set([
  ColumnType.String,
  ColumnType.Number,
  ColumnType.BigInt,
  ColumnType.Boolean,
  ColumnType.Timestamp,
  ColumnType.Date,
]);
export const REALTIME_LIST_ARRAY_COLUMN_TYPES: ReadonlySet<string> = new Set([
  ColumnType.JsonArrayStrings,
  ColumnType.JsonArrayNumbers,
]);

/**
 * Windows at or below this length are servable from the stream ring alone;
 * anything longer (and lifetime) requires warehouse backing. Mirrors
 * `streamOnlyWindowMaxDuration` in `realtime/internal/trait/definition.go`.
 */
export const REALTIME_TRAIT_STREAM_ONLY_MAX_WINDOW_SECONDS = 24 * 60 * 60;

const REALTIME_TRAIT_WINDOW_UNIT_SECONDS: Record<
  RealtimeTraitSlidingWindowUnit,
  number
> = {
  minute: 60,
  hour: 60 * 60,
  day: 24 * 60 * 60,
};

/** Seconds per sliding-window unit. */
export function realtimeTraitWindowUnitSeconds(
  unit: RealtimeTraitSlidingWindowUnit,
): number {
  return REALTIME_TRAIT_WINDOW_UNIT_SECONDS[unit];
}

/**
 * Window duration in seconds; null for lifetime (an absent window means
 * lifetime), undefined for malformed windows.
 */
export function realtimeTraitWindowSeconds(
  window: RealtimeTraitWindow | undefined,
): number | null | undefined {
  if (window == null || window.type === "lifetime") {
    return null;
  }
  if (window.type !== "sliding") {
    return undefined;
  }
  const { direction, interval, quantity } = window.value ?? {};
  const unitSeconds = REALTIME_TRAIT_WINDOW_UNIT_SECONDS[interval];
  if (
    direction !== "backward" ||
    unitSeconds == null ||
    !Number.isInteger(quantity) ||
    quantity <= 0
  ) {
    return undefined;
  }
  return quantity * unitSeconds;
}

/**
 * True for lifetime windows and sliding windows longer than the stream-only
 * maximum. Malformed windows return false — they're unservable either way.
 */
export function realtimeTraitWindowRequiresWarehouseBacking(
  window: RealtimeTraitWindow | undefined,
): boolean {
  const seconds = realtimeTraitWindowSeconds(window);
  return (
    seconds === null ||
    (typeof seconds === "number" &&
      seconds > REALTIME_TRAIT_STREAM_ONLY_MAX_WINDOW_SECONDS)
  );
}

/**
 * How the warehouse baseline for a realtime trait is produced. The customer
 * picks one backing model, and every mapped column lives on it — so the
 * orderBy and freshness timestamp always describe the same row as the value.
 */
export type RealtimeTraitWarehouseBacking =
  | ColumnWarehouseBacking
  | EventsWarehouseBacking;

/**
 * Copies the customer's precomputed column(s) from the backing model. Not
 * available to Average: a precomputed average cannot be merged with the
 * stream, so Average baselines are always recomputed from an event model.
 */
export type ColumnWarehouseBacking = {
  mode: "column";

  /**
   * The backing model: the parent model itself, or a model merged into it
   * through a to-one relationship.
   */
  modelId: string;

  /**
   * Relationship path from the parent model to the backing model; empty when
   * the backing model is the parent model itself.
   */
  path: string[];

  /** The precomputed value column to copy. */
  selectColumn: string;

  /**
   * For First/Last: the orderBy of the precomputed winner (e.g.
   * `last_purchase_at` next to `last_purchase_amount`). Required when the
   * trait's orderBy differs from its toSelect; Max/Min-shaped values are
   * their own orderBy.
   */
  orderByColumn?: string;
};

/**
 * Recomputes the baseline from a warehouse event model related to the parent
 * model, aggregated by Hightouch at sync time. (Realtime event models are
 * stream-schema stubs with no warehouse table behind them, so the trait's own
 * event model is never the target.) Its registered
 * `event_models.timestamp_column` is the time axis: the baseline is cut at
 * the table-wide hour-floored anchor `T` (`< T` for lifetime Count/Sum,
 * `[T - window, T)` for >24h sliding windows), and the destination stamps
 * `T` onto written rows as the merge watermark. Only Count/Sum/First/Last
 * traits can be events-backed.
 */
export type EventsWarehouseBacking = {
  mode: "events";

  /** model_relationships.id from the parent model to the event model. */
  relationshipId: string;

  /**
   * The column to aggregate: the SUM or AVG input, or the First/Last
   * winner's value. Absent for Count (COUNT(*)).
   */
  selectColumn?: string;

  /** First/Last winner ordering column. */
  orderByColumn?: string;

  /**
   * The trait's filter conditions restated against the event model's columns
   * — the trait's own conditions reference stream fields and never apply
   * here. Keeping the two predicates equivalent is on the customer, exactly
   * like a precomputed column encoding the filter in column mode.
   */
  conditions?: TraitCondition[];
};

// Config for realtime traits.
export type RealtimeTraitConfig = {
  /** Absent means lifetime. Worker and PAPI must derive identical layouts. */
  window?: RealtimeTraitWindow;

  /**
   * Sole authority on warehouse backing — absent means the trait has no
   * warehouse baseline.
   */
  warehouseBacking?: RealtimeTraitWarehouseBacking;

  /** List traits only fields */
  operations?: RealtimeListOperation[];
  /** Absent means 25 (the backend max). */
  maxItems?: number;
  /** Absent means 32 (the backend max). */
  maxItemBytes?: number;
};

export type CountTraitConfig = {
  column?: RawColumn | RelatedColumn;
  conditions?: TraitCondition[];

  /**
   * Realtime trait config. undefined if this isn't a realtime trait.
   */
  realtimeConfig?: RealtimeTraitConfig;
};

export type SumTraitConfig = {
  column: RawColumn | RelatedColumn;
  conditions?: TraitCondition[];

  /**
   * Realtime trait config. undefined if this isn't a realtime trait.
   */
  realtimeConfig?: RealtimeTraitConfig;
};

export type AverageTraitConfig = {
  column: RawColumn | RelatedColumn;
  conditions?: TraitCondition[];

  /**
   * Realtime trait config. undefined if this isn't a realtime trait.
   */
  realtimeConfig?: RealtimeTraitConfig;
};

// Used by MostFrequent and LeastFrequent trait types
export type CountDedupedTraitConfig = {
  toSelect: RawColumn | RelatedColumn;
  conditions?: TraitCondition[];
};

// Used by First and Last trait types
export type OrderDedupedTraitConfig = {
  orderBy: RawColumn | RelatedColumn;
  toSelect: RawColumn | RelatedColumn;
  conditions?: TraitCondition[];

  /**
   * Realtime trait config. undefined if this isn't a realtime trait.
   */
  realtimeConfig?: RealtimeTraitConfig;
};

export type RawSqlTraitConfig = {
  // The raw SQL for the aggregation. Columns must be referred to with `{{column "name"}}`.
  // For example:  `COUNT(DISTINCT {{column "product_id"}})`
  aggregation: string;

  // The type of the result of the trait. This is used to determine what
  // property operators are compatible with the trait.
  resultingType: ColumnType;

  // The raw SQL for the trait's default value (i.e. if the trait evaluate to NULL).
  // An empty string is treated as NULL.
  // Note that string literals must be quoted appropriately.
  defaultValue?: string;

  conditions?: TraitCondition[];
};

export type FormulaTraitConfig = {
  transformation: string;

  resultingType: ColumnType;

  // Empty string or undefined is treated as NULL
  defaultValue?: string;

  referencedColumns: {
    alias: string;
    column: Column;
  }[];

  // Include this to make the typechecking consistent everywhere.
  // But set it to undefined because we cant't add filters to a formula trait.
  conditions?: undefined;
};

// Mirrored by FormulaExpr in realtime/internal/trait/formula.go; the worker and PAPI both evaluate it.
export type RealtimeFormulaExpression =
  | { op: "ref"; traitId: string }
  | { op: "literal"; value: number }
  | {
      op: "add" | "sub" | "mul" | "div";
      args: [RealtimeFormulaExpression, RealtimeFormulaExpression];
    };

// Arithmetic over realtime numeric traits on the same parent model. Never compiled to SQL.
export type RealtimeFormulaTraitConfig = {
  expression: RealtimeFormulaExpression;
  // Served when the formula is null. An unresolvable formula stays unresolvable rather than reporting it.
  defaultValue?: number;
  conditions?: undefined;
};

// Must match maxFormulaTerms in realtime/internal/trait/formula.go.
export const MAX_REALTIME_FORMULA_TERMS = 100;

// Must match isNumericTrait in realtime/internal/trait/formula.go.
export const REALTIME_FORMULA_OPERAND_TYPES: ReadonlySet<string> = new Set([
  TraitType.Count,
  TraitType.Sum,
  TraitType.Average,
  TraitType.First,
  TraitType.Last,
]);

const REALTIME_FORMULA_BINARY_OPS: ReadonlySet<unknown> = new Set([
  "add",
  "sub",
  "mul",
  "div",
]);

export function parseRealtimeFormulaExpression(
  expression: unknown,
): RealtimeFormulaExpression | undefined {
  return isValidRealtimeFormulaNode(expression, { operatorCount: 0 })
    ? (expression as RealtimeFormulaExpression)
    : undefined;
}

export function parseRealtimeFormulaConfig(
  config: unknown,
): RealtimeFormulaTraitConfig | undefined {
  if (config == null || typeof config !== "object") {
    return undefined;
  }
  const { expression, defaultValue } = config as Record<string, unknown>;
  const parsedExpression = parseRealtimeFormulaExpression(expression);
  if (!parsedExpression) {
    return undefined;
  }
  if (defaultValue == null) {
    return { expression: parsedExpression };
  }
  return typeof defaultValue === "number" && Number.isFinite(defaultValue)
    ? { expression: parsedExpression, defaultValue }
    : undefined;
}

function isValidRealtimeFormulaNode(
  node: unknown,
  counts: { operatorCount: number },
): boolean {
  if (node == null || typeof node !== "object") {
    return false;
  }
  const { op, traitId, value, args } = node as Record<string, unknown>;
  if (op === "ref") {
    return typeof traitId === "string" && traitId !== "";
  }
  if (op === "literal") {
    return typeof value === "number" && Number.isFinite(value);
  }
  if (
    !REALTIME_FORMULA_BINARY_OPS.has(op) ||
    !Array.isArray(args) ||
    args.length !== 2
  ) {
    return false;
  }
  // A formula has one fewer operator than terms, and counting before recursing also caps the depth.
  counts.operatorCount += 1;
  return (
    counts.operatorCount < MAX_REALTIME_FORMULA_TERMS &&
    args.every((arg) => isValidRealtimeFormulaNode(arg, counts))
  );
}

export function realtimeFormulaOperandIds(
  expression: RealtimeFormulaExpression,
): string[] {
  const operandIds = new Set<string>();
  const visit = (node: RealtimeFormulaExpression) => {
    if (node.op === "ref") {
      operandIds.add(node.traitId);
    } else if (node.op !== "literal") {
      node.args.forEach(visit);
    }
  };
  visit(expression);
  return [...operandIds];
}

export type ListTraitConfig = {
  toSelect?: RawColumn | RelatedColumn;
  toSelectList?: (RawColumn | RelatedColumn)[];
  unique?: boolean;
  orderBy?: {
    column: RawColumn | RelatedColumn;
    direction: "ASC" | "DESC";
  };
  slice?: {
    location: "FIRST" | "LAST";
    maxResults?: number;
  };
  conditions?: TraitCondition[];
  resultingType:
    | ColumnType.JsonArrayNumbers
    | ColumnType.JsonArrayStrings
    | ColumnType.Json
    // When singleObject is true, resultingType can be a scalar type for single-column traits
    | ColumnType.String
    | ColumnType.Number
    | ColumnType.BigInt
    | ColumnType.Boolean
    | ColumnType.Timestamp
    | ColumnType.Date;
  // When true and slice.maxResults is 1, return a single JSON object instead of an array.
  // This "flattens" the result from [{...}] to {...}
  singleObject?: boolean;

  // Undefined if this isn't a realtime trait.
  realtimeConfig?: RealtimeTraitConfig;
};

/** The resultingTypes a List trait may have when it compiles to an array aggregation. */
export const LIST_TRAIT_ARRAY_RESULTING_TYPES: ReadonlySet<ColumnType> =
  new Set([ColumnType.JsonArrayStrings, ColumnType.JsonArrayNumbers]);

/**
 * Returns why a List trait config's saved `resultingType` disagrees with the
 * shape its compiled SQL will produce, or null when they're consistent.
 *
 * The compiled SQL is an array aggregation unless `singleObject` is true (in
 * which case the first element is extracted). A scalar/object `resultingType`
 * without `singleObject: true` means downstream consumers treat the compiled
 * array as a scalar — e.g. audience date conditions wrap the ARRAY_AGG alias
 * in DATE_TRUNC and the warehouse rejects it with a type mismatch.
 *
 * Configs without a `resultingType` (saved before it existed) and realtime
 * list traits (not compiled through the warehouse array aggregation) are not
 * checked.
 */
export function getListTraitConfigError(
  config:
    | Pick<
        ListTraitConfig,
        "resultingType" | "singleObject" | "slice" | "realtimeConfig"
      >
    | null
    | undefined,
  type: TraitType | string | null | undefined,
): string | null {
  if (type !== TraitType.List || config == null) {
    return null;
  }

  const { resultingType, singleObject, slice } = config;
  if (resultingType == null || config.realtimeConfig != null) {
    return null;
  }
  if (singleObject && slice?.maxResults !== 1) {
    return "a List trait returning a single object must limit the list to 1 item";
  }
  if (singleObject && LIST_TRAIT_ARRAY_RESULTING_TYPES.has(resultingType)) {
    return `a List trait returning a single object cannot have the array resultingType "${resultingType}"`;
  }
  if (!singleObject && !LIST_TRAIT_ARRAY_RESULTING_TYPES.has(resultingType)) {
    return `a List trait returning a list with more than 1 item cannot have the scalar resultingType "${resultingType}"`;
  }
  return null;
}

export type TrackedAudienceMembershipTraitConfig = {
  // The list row's PK is the trait_definitions.id, so no separate list id
  // is stored here — query-build joins tracked_audience_lists on trait.id.
  // Per-list settings (freshness, output format, which keys should be in
  // the object) and the audience allowlist live in tracked_audience_lists /
  // tracked_audience_list_audiences.
  resultingType: ColumnType.Json | ColumnType.JsonArrayStrings;

  // Filters aren't supported for this trait type — membership is fully
  // determined by the allowlist + audience_membership snapshot. Declared to
  // keep the shape uniform across the TraitConfig union.
  conditions?: TraitCondition[];
};

export type TrackedAudienceListOutputFormat = "id" | "object";

/**
 * One audience contributing to a tracked-audience-membership trait. The name is
 * resolved from segments at query time (the warehouse changelog only stores
 * ids). It's never null, due to ON DELETE CASCADE of audience rows.
 */
export type TrackedAudienceListEntry = {
  id: string;
  name: string;
};

/**
 * The trait's stored resulting column type is derived from the list's output
 * format: "object" emits an array of objects (Json) — each carrying
 * audience_id, plus audience_name and/or split_group when the list opts into
 * them — and "id" emits an array of audience-id strings (JsonArrayStrings).
 * The opt-in keys don't affect the resulting type, only which keys each object
 * carries. Kept as a single helper so the create and edit paths can never
 * derive it differently.
 */
export const trackedAudienceMembershipResultingType = (
  outputFormat: TrackedAudienceListOutputFormat,
): ColumnType.Json | ColumnType.JsonArrayStrings =>
  outputFormat === "object" ? ColumnType.Json : ColumnType.JsonArrayStrings;

export type PredictiveTraitConfig = {
  conditions?: TraitCondition[];
  // Always a number for now.
  resultingType: ColumnType.Number;
  // Whether to compare to the percentile or the raw score (probability)
  computationType?: "percentile" | "score";
  // Determines which column to aggregate: "rank" for catalog recs, "percentile"/"score" for propensity
  predictorType?: "propensity" | "catalog_recommendation" | "lookalike";
};

export type AggregationTraitConfig =
  | CountTraitConfig
  | SumTraitConfig
  | AverageTraitConfig;

export const isAggregationTraitConfig = (
  traitType: TraitType,
  _config: TraitConfig,
): _config is AggregationTraitConfig => {
  return (
    traitType === TraitType.Sum ||
    traitType === TraitType.Count ||
    traitType === TraitType.Average
  );
};

export const isCountDedupedTraitConfig = (
  traitType: TraitType,
  _config: TraitConfig,
): _config is CountDedupedTraitConfig => {
  return (
    traitType === TraitType.LeastFrequent ||
    traitType === TraitType.MostFrequent
  );
};

export const isOrderDedupedTraitConfig = (
  traitType: TraitType,
  _config: TraitConfig,
): _config is OrderDedupedTraitConfig => {
  return traitType === TraitType.First || traitType === TraitType.Last;
};

export const isFormulaTraitConfig = (
  traitType: TraitType,
  _config: TraitConfig,
): _config is FormulaTraitConfig => {
  return traitType === TraitType.Formula;
};

export const isVariableResultingTypeTraitConfig = (
  traitType: TraitType,
  _config: TraitConfig,
): _config is RawSqlTraitConfig | FormulaTraitConfig | ListTraitConfig => {
  return traitType === TraitType.RawSql || traitType === TraitType.Formula;
};

export const isCatalogRecommendationTraitConfig = (
  traitType: TraitType,
  config: TraitConfig,
): config is PredictiveTraitConfig =>
  traitType === TraitType.Predictive &&
  (config as PredictiveTraitConfig).predictorType === "catalog_recommendation";
